import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30; // 60s total

/**
 * Billing success page — polls the checkout status until paid or timeout.
 * Activated subscription is reflected by /billing/status (period_end pushed forward).
 */
export default function BillingSuccess() {
  const { lang } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  const [state, setState] = useState({ status: "polling", attempts: 0 });
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", message: "Missing session_id" });
      return undefined;
    }
    let timer = null;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/billing/checkout/status/${sessionId}`);
        const { payment_status, status } = r.data;
        setPlan(r.data.plan);
        if (payment_status === "paid" && status === "complete") {
          setState({ status: "paid", attempts });
          return;
        }
        if (status === "expired" || payment_status === "failed") {
          setState({ status: "failed", attempts });
          return;
        }
        if (attempts >= MAX_POLLS) {
          setState({ status: "timeout", attempts });
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        setState({ status: "error", message: e?.response?.data?.detail || "Error" });
      }
    };
    poll();
    return () => { if (timer) clearTimeout(timer); };
  }, [sessionId]);

  const content = (() => {
    if (state.status === "paid") {
      return {
        icon: <CheckCircle2 className="w-16 h-16 text-emerald-400" />,
        title: lang === "es" ? "¡Pago confirmado!" : "Pagament confirmat!",
        body: lang === "es"
          ? "Tu suscripción está activa. Ya puedes empezar a usar todas las funciones."
          : "La teva subscripció està activa. Ja pots començar a utilitzar totes les funcions.",
        cta: lang === "es" ? "Ir al panel" : "Anar al tauler",
        next: "/app",
      };
    }
    if (state.status === "failed") {
      return {
        icon: <XCircle className="w-16 h-16 text-rose-400" />,
        title: lang === "es" ? "Pago no completado" : "Pagament no completat",
        body: lang === "es"
          ? "El pago ha sido cancelado o ha fallado. Puedes intentarlo de nuevo."
          : "El pagament s'ha cancel·lat o ha fallat. Pots tornar-ho a intentar.",
        cta: lang === "es" ? "Ver planes" : "Veure plans",
        next: "/app/pricing",
      };
    }
    if (state.status === "timeout") {
      return {
        icon: <Loader2 className="w-16 h-16 text-amber-400 animate-spin" />,
        title: lang === "es" ? "Estamos comprobando tu pago" : "Estem comprovant el pagament",
        body: lang === "es"
          ? "Esto puede tardar un poco. Si has completado el pago, recarga la página en unos segundos."
          : "Això pot trigar una mica. Si has completat el pagament, recarrega la pàgina en uns segons.",
        cta: lang === "es" ? "Recargar" : "Recarregar",
        next: null,
      };
    }
    if (state.status === "error") {
      return {
        icon: <XCircle className="w-16 h-16 text-rose-400" />,
        title: lang === "es" ? "Error" : "Error",
        body: state.message,
        cta: lang === "es" ? "Ver planes" : "Veure plans",
        next: "/app/pricing",
      };
    }
    return {
      icon: <Loader2 className="w-16 h-16 text-blue-400 animate-spin" />,
      title: lang === "es" ? "Procesando pago..." : "Processant pagament...",
      body: lang === "es"
        ? "No cierres esta ventana. Estamos verificando tu pago con Stripe."
        : "No tanquis aquesta finestra. Estem verificant el pagament amb Stripe.",
      cta: null,
      next: null,
    };
  })();

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6"
         data-testid="billing-success-page">
      <div className="card-surface max-w-md w-full p-8 text-center space-y-5">
        <div className="flex justify-center">{content.icon}</div>
        <h1 className="font-display text-2xl font-bold text-white">{content.title}</h1>
        <p className="text-slate-400 text-sm leading-relaxed">{content.body}</p>
        {plan && state.status === "paid" && (
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-xs">
            <span className="capitalize">{plan}</span>
          </div>
        )}
        {content.cta && (
          <Button
            onClick={() => content.next ? nav(content.next) : window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="billing-success-cta"
          >
            {content.cta}
            {content.next && <ArrowRight size={14} className="ml-2" />}
          </Button>
        )}
      </div>
    </div>
  );
}
